import { jsx as _jsx } from "react/jsx-runtime";
import { afterAnimationFrame } from '@prophecy/utils/dom';
import { KeyCodes } from '@prophecy/utils/keyboard';
import { useOnMount, usePersistentCallback } from '@prophecy/utils/react/hooks';
import { PlateElement, useElement } from '@udecode/plate/react';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { SELECT_SELECTION_SEARCH_CLASS } from '../Select/tokens';
import { StyledMentionAutoComplete } from './styled';
import { useActiveOption } from './useActiveOption';
function TriggerInputElementInner({ trigger, getSuggestions = ()=>Promise.resolve([]), renderSuggestion, onSelect: onSelectProp, onTrigger, onActiveOptionChange, endCharRegex, editorContainerRef, dropdownMatchSelectWidth = 300, dataTestId = 'trigger-autocomplete', ...plateProps }, ref) {
    const { editor } = plateProps;
    const [search, setSearch] = useState(trigger);
    const autoCompleteWrapperRef = useRef(null);
    const element = useElement();
    const removed = useRef(false);
    const [suggestions, setSuggestions] = useState([]);
    const persistentGetSuggestions = usePersistentCallback(getSuggestions);
    useEffect(()=>{
        const searchText = search.startsWith(trigger) ? search.slice(trigger.length) : search;
        persistentGetSuggestions(searchText).then(setSuggestions);
    }, [
        persistentGetSuggestions,
        search,
        trigger
    ]);
    const removeElement = ()=>{
        const path = editor.api.findPath(element);
        if (!path) return;
        editor.tf.removeNodes({
            at: path
        });
        editorContainerRef.current?.focus();
    };
    // we use data-value on input wrapper to control the input width
    // We need to manually update the attribute data-value to avoid a react cycle causing flicker.
    const updateParentAttribute = (value)=>{
        autoCompleteWrapperRef.current?.querySelector(`.${SELECT_SELECTION_SEARCH_CLASS}`)?.setAttribute('data-value', value);
    };
    useOnMount(()=>{
        afterAnimationFrame(()=>{
            autoCompleteWrapperRef.current?.querySelector('input')?.focus();
        });
        updateParentAttribute(search);
        onTrigger?.();
    });
    const options = useMemo(()=>suggestions.map((suggestion)=>({
                ...suggestion,
                value: `${trigger}${suggestion.value}`,
                key: suggestion.value
            })), [
        suggestions,
        trigger
    ]);
    const cleanValue = (option)=>({
            ...option,
            value: option.value.startsWith(trigger) ? option.value.slice(1) : option.value
        });
    useActiveOption(autoCompleteWrapperRef, options, cleanValue, onActiveOptionChange);
    const handleSelect = (option)=>{
        if (removed.current) return;
        removed.current = true;
        const currentSearch = search;
        updateParentAttribute('');
        setSearch('');
        removeElement();
        onActiveOptionChange?.(undefined);
        onSelectProp(option ? cleanValue(option) : undefined, currentSearch);
    };
    return /*#__PURE__*/ _jsx(PlateElement, {
        ref: ref,
        as: "span",
        ...plateProps,
        children: /*#__PURE__*/ _jsx("span", {
            contentEditable: false,
            ref: autoCompleteWrapperRef,
            children: /*#__PURE__*/ _jsx(StyledMentionAutoComplete, {
                options: options,
                defaultOpen: true,
                defaultActiveFirstOption: true,
                value: search,
                searchValue: search,
                showSearch: false,
                filterOption: false,
                onBlur: ()=>handleSelect(),
                onSearch: updateParentAttribute,
                optionRender: renderSuggestion ? (option)=>renderSuggestion(cleanValue(option.data)) : undefined,
                onChange: (value)=>{
                    setSearch(value);
                    updateParentAttribute(value);
                },
                onKeyDown: (e)=>{
                    const { key } = e;
                    e.stopPropagation();
                    if (key === 'Enter') {
                        const item = options.find((o)=>o.value.toLowerCase().includes(search.toLowerCase()));
                        if (item) {
                            handleSelect(item);
                        } else {
                            if (removed.current) return;
                            removed.current = true;
                            onActiveOptionChange?.(undefined);
                            const currentSearch = search;
                            updateParentAttribute('');
                            setSearch('');
                            // remove the special node and make it plain text
                            const path = editor.api.findPath(element);
                            if (path) {
                                editor.tf.removeNodes({
                                    at: path
                                });
                                editor.tf.insertText(currentSearch);
                            }
                            // trigger a normal enter event on the editor container so the prompt gets sent
                            afterAnimationFrame(()=>{
                                editorContainerRef.current?.focus();
                                editorContainerRef.current?.dispatchEvent(new KeyboardEvent('keydown', {
                                    key: KeyCodes.Enter,
                                    code: KeyCodes.Enter,
                                    bubbles: true,
                                    cancelable: true,
                                    shiftKey: e.shiftKey
                                }));
                            });
                        }
                    } else if (endCharRegex?.test(key)) {
                        // trigger manual blur so the input becomes a mention
                        autoCompleteWrapperRef.current?.querySelector('input')?.blur();
                    } else if (key === 'Escape') {
                        e.preventDefault();
                        removed.current = true;
                        onActiveOptionChange?.(undefined);
                        removeElement();
                    } else if (key === 'Backspace' && search === trigger) {
                        e.preventDefault();
                        removed.current = true;
                        onActiveOptionChange?.(undefined);
                        removeElement();
                    }
                //TODO: handle arrow left and right while the user is inside the mention input
                },
                onSelect: (_, option)=>{
                    handleSelect(option);
                },
                dropdownMatchSelectWidth: dropdownMatchSelectWidth,
                disableScrollClose: true,
                "data-test-id": dataTestId
            })
        })
    });
}
export const TriggerInputElement = /*#__PURE__*/ forwardRef(TriggerInputElementInner);
